import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import {
  CheckCircle,
  Close,
  Search,
  AttachMoney,
  CreditCard,
  Receipt,
  Person,
  Description,
} from "@mui/icons-material";
import { API_CONFIG } from "../../config/environment";
import { useAuth } from "../../context/context";
import directSalesService from "../../services/directSalesService";

const DirectSaleModal = ({
  open,
  onClose,
  product,
  onSaleCreated,
  saleToEdit = null,
}) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Detectar modo: "create" o "edit"
  const isEditMode = !!saleToEdit;

  // Estados del formulario
  const [buyerSearch, setBuyerSearch] = useState(""); // Inicializar como string vacío
  const [buyerResults, setBuyerResults] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [searchingBuyers, setSearchingBuyers] = useState(false);

  const [formData, setFormData] = useState({
    finalPrice: product?.price || 0,
    commission: "",
    paymentMethod: "transferencia",
    transactionReference: "",
    notes: "",
  });

  // Resetear formulario cuando cambie el producto o se abra el modal
  useEffect(() => {
    if (open) {
      if (isEditMode && saleToEdit) {
        setFormData({
          finalPrice: saleToEdit.finalPrice || 0,
          commission: saleToEdit.commission || "",
          paymentMethod: saleToEdit.paymentMethod || "transferencia",
          transactionReference: saleToEdit.transactionReference || "",
          notes: saleToEdit.notes || "",
        });
        setSelectedBuyer(saleToEdit.buyer || null);
      } else if (product) {
        setFormData({
          finalPrice: product.price || 0,
          commission: "",
          paymentMethod: "transferencia",
          transactionReference: "",
          notes: "",
        });
        setSelectedBuyer(null);
      }
      setBuyerSearch(""); // Asegurar que siempre sea string
      setBuyerResults([]);
      setError(null);
      setSuccess(false);
    }
  }, [open, product, saleToEdit, isEditMode]);

  // Buscar compradores con debounce
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (buyerSearch.length >= 3) {
        searchBuyers(buyerSearch);
      } else {
        setBuyerResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [buyerSearch]);

  const searchBuyers = async (query) => {
    setSearchingBuyers(true);
    try {
      const response = await fetch(
        `${API_CONFIG.V2_BASE_URL}/bienes/users/search?q=${encodeURIComponent(
          query
        )}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error al buscar compradores");
      }

      const data = await response.json();
      const users = data.users || [];
      setBuyerResults(users);

      console.log(`✅ Se encontraron ${users.length} comprador(es)`);
    } catch (err) {
      console.error("Error buscando compradores:", err);
      setBuyerResults([]);
    } finally {
      setSearchingBuyers(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.finalPrice || formData.finalPrice <= 0) {
      setError("El precio final debe ser mayor a 0");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        finalPrice: parseFloat(formData.finalPrice),
        commission: formData.commission
          ? parseFloat(formData.commission)
          : null,
        paymentMethod: formData.paymentMethod,
        transactionReference: formData.transactionReference || null,
        notes: formData.notes || null,
      };

      // Solo agregar buyerId si hay un comprador seleccionado
      if (selectedBuyer) {
        payload.buyerId = selectedBuyer.id;
      }

      // Solo incluir lotId si estamos creando una nueva venta
      if (!isEditMode) {
        payload.lotId = product.id;
        payload.soldAt = new Date().toISOString();
      }

      const response = isEditMode
        ? await directSalesService.updateDirectSale(
            saleToEdit.id,
            payload,
            token
          )
        : await directSalesService.createDirectSale(payload, token);

      setSuccess(true);

      // Esperar 2 segundos para mostrar el mensaje de éxito
      setTimeout(() => {
        if (onSaleCreated) {
          onSaleCreated(response.data);
        }
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircle color="success" />
          <Typography variant="h6" component="span">
            {isEditMode ? "Editar Venta Directa" : "Registrar Venta Directa"}
          </Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {/* Información del Producto */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: "#f5f9ff" }}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            📦 Producto a vender
          </Typography>
          <Typography variant="span" gutterBottom>
            {product?.name}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mt: 1,
              width: "fit-content",
            }}
          >
            <Chip
              label={`Precio de lista: $${product?.price?.toLocaleString(
                "es-CL"
              )}`}
              color="primary"
              size="small"
            />
            <Chip label={product?.category} size="small" variant="outlined" />
          </Box>
        </Paper>

        {/* Mensajes de error/éxito */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            ✅ ¡Venta {isEditMode ? "actualizada" : "registrada"} exitosamente!
            El producto se ha marcado como vendido.
          </Alert>
        )}

        {/* Formulario */}
        <Box component="form" onSubmit={handleSubmit}>
          {/* Buscar Comprador */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Person fontSize="small" />
              Comprador{" "}
              <span style={{ color: "gray", fontWeight: "normal" }}>
                (opcional)
              </span>
            </Typography>

            {!selectedBuyer ? (
              <Autocomplete
                key={`buyer-search-${open}`} // Force remount when modal opens
                options={buyerResults}
                loading={searchingBuyers}
                value={null}
                inputValue={buyerSearch}
                getOptionLabel={(option) => {
                  if (!option || typeof option === "string") return "";
                  // Usar estructura aplanada si existe, sino usar la anidada
                  return option.fullName || option.email || "";
                }}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                filterOptions={(x) => x} // Disable client-side filtering
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="body2">
                        <strong>{option.fullName || "Sin nombre"}</strong>
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        📧 {option.email}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        🆔 RUT: {option.rut || "N/A"} • ID: {option.id}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Buscar por nombre, RUT o email..."
                    helperText={
                      buyerResults.length > 0
                        ? `${buyerResults.length} resultado(s) encontrado(s)`
                        : buyerSearch.length >= 3 && !searchingBuyers
                        ? "No se encontraron compradores"
                        : "Escribe al menos 3 caracteres"
                    }
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {searchingBuyers ? (
                            <CircularProgress size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                onInputChange={(event, newValue, reason) => {
                  // Solo actualizar si el evento viene del usuario escribiendo
                  if (reason === "input") {
                    setBuyerSearch(newValue || "");
                  }
                }}
                onChange={(event, newValue) => {
                  if (newValue && typeof newValue === "object") {
                    setSelectedBuyer(newValue);
                    setBuyerSearch("");
                    setBuyerResults([]);
                  }
                }}
                noOptionsText={
                  buyerSearch.length < 3
                    ? "Escribe al menos 3 caracteres"
                    : searchingBuyers
                    ? "Buscando..."
                    : "No se encontraron compradores"
                }
                ListboxProps={{
                  style: { maxHeight: "300px" },
                }}
              />
            ) : (
              <Paper
                elevation={1}
                sx={{ p: 2, bgcolor: "#e8f5e9", position: "relative" }}
              >
                <Typography variant="body2">
                  <strong>
                    {selectedBuyer.fullName ||
                      selectedBuyer.name +
                        " " +
                        selectedBuyer.lastName +
                        " " +
                        selectedBuyer.secondLastName ||
                      "Sin nombre"}
                  </strong>
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  📧 {selectedBuyer.email}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  🆔 RUT: {selectedBuyer.rut || "N/A"}
                </Typography>
                {selectedBuyer.cellphone && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    📱 {selectedBuyer.cellphone}
                  </Typography>
                )}
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setSelectedBuyer(null);
                    setBuyerSearch("");
                    setBuyerResults([]);
                  }}
                  sx={{ position: "absolute", top: 8, right: 8 }}
                >
                  Cambiar
                </Button>
              </Paper>
            )}
          </Box>

          {/* Precio Final */}
          <TextField
            fullWidth
            label="Precio Final de Venta"
            type="number"
            required
            value={formData.finalPrice}
            onChange={(e) =>
              setFormData({ ...formData, finalPrice: e.target.value })
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoney />
                </InputAdornment>
              ),
            }}
            helperText="Precio acordado con el comprador (sin IVA)"
            sx={{ mb: 2 }}
          />

          {/* Método de Pago */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Método de Pago *</InputLabel>
            <Select
              value={formData.paymentMethod}
              onChange={(e) =>
                setFormData({ ...formData, paymentMethod: e.target.value })
              }
              label="Método de Pago *"
              MenuProps={{ disableScrollLock: true }}
              startAdornment={
                <InputAdornment position="start">
                  <CreditCard />
                </InputAdornment>
              }
            >
              <MenuItem value="transferencia">Transferencia Bancaria</MenuItem>
              <MenuItem value="efectivo">Efectivo</MenuItem>
              <MenuItem value="cheque">Cheque</MenuItem>
              <MenuItem value="credito">Crédito Directo</MenuItem>
              <MenuItem value="otro">Otro</MenuItem>
            </Select>
          </FormControl>

          {/* Referencia de Transacción */}
          <TextField
            fullWidth
            label="Referencia de Transacción (opcional)"
            value={formData.transactionReference}
            onChange={(e) =>
              setFormData({ ...formData, transactionReference: e.target.value })
            }
            placeholder="Ej: TRANSF-20251103-001"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Receipt />
                </InputAdornment>
              ),
            }}
            helperText="Número de transferencia, cheque, o identificador de pago"
            sx={{ mb: 2 }}
          />

          {/* Comisión */}
          <TextField
            fullWidth
            label="Comisión (opcional)"
            type="number"
            value={formData.commission}
            onChange={(e) =>
              setFormData({ ...formData, commission: e.target.value })
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoney />
                </InputAdornment>
              ),
            }}
            helperText="Comisión generada por la venta (si aplica)"
            sx={{ mb: 2 }}
          />

          {/* Notas */}
          <TextField
            fullWidth
            label="Notas Adicionales (opcional)"
            multiline
            rows={3}
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
            placeholder="Observaciones sobre la venta, condiciones especiales, etc."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Description />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Advertencia */}
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>⚠️ Advertencia:</strong> Al confirmar, el producto será
              marcado como VENDIDO y dejará de aparecer en el catálogo público
              en 7 días.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button variant="outlined" onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={loading || success}
        >
          {loading ? "Registrando..." : isEditMode ? "Actualizar" : "Confirmar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DirectSaleModal;
